import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, MultiSelect, Select, toast } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type PrintSettingsLineDTO } from "../../api";
import { TwoLine } from "../../components/cells";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * One print-settings profile and the (product × production centre) pairs that
 * resolve to it.
 *
 * The add dialog only offers pairs that don't exist yet — the Vaadin form
 * filtered the product list per centre against every existing line, and a
 * duplicate pair would make the resolver ambiguous.
 */
export function PrintSettingsDetailPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const { id } = useParams({ from: "/config/print-settings/$id" });
  const settingsId = Number(id);
  const allowed = useAllowed(LogicView.CONFIG_PRINT_SETTINGS);

  const [adding, setAdding] = useState(false);
  const [centerId, setCenterId] = useState("");
  const [productIds, setProductIds] = useState<string[]>([]);

  const settings = useQuery({
    queryKey: ["print-settings", settingsId],
    queryFn: () => api.printSettings.findById(settingsId),
    enabled: allowed && Number.isFinite(settingsId),
  });

  const lines = useQuery({
    queryKey: ["print-settings", settingsId, "lines"],
    queryFn: () => api.printSettingsLines.getByPrintSettings(settingsId),
    enabled: allowed && Number.isFinite(settingsId),
  });

  const allLines = useQuery({
    queryKey: ["print-settings", "lines", "all"],
    queryFn: () => api.printSettingsLines.findAll(),
    enabled: allowed,
  });

  const centers = useQuery({
    queryKey: ["production", "center"],
    queryFn: () => api.productionCenters.findAll(),
    enabled: allowed,
  });

  const products = useQuery({
    queryKey: ["product"],
    queryFn: () => api.products.findAll(),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });

  const centerById = useMemo(
    () => new Map((centers.data ?? []).map((entry) => [entry.id, entry])),
    [centers.data],
  );
  const productById = useMemo(
    () => new Map((products.data ?? []).map((entry) => [entry.id, entry])),
    [products.data],
  );

  /** Products with no line on the picked centre — the only valid additions. */
  const availableProducts = useMemo(() => {
    if (!centerId) return [];
    const taken = new Set(
      (allLines.data ?? [])
        .filter((line) => line.production_center_id === Number(centerId))
        .map((line) => line.product_id),
    );
    return (products.data ?? [])
      .filter((product) => !taken.has(product.id))
      .map((product) => ({ value: String(product.id), label: product.code }));
  }, [allLines.data, centerId, products.data]);

  const cols = useMemo(() => {
    const c = defineColumns<PrintSettingsLineDTO>();
    return [
      c.text(
        "production_center",
        (row) => centerById.get(row.production_center_id)?.name ?? `#${row.production_center_id}`,
        { headerKey: "column.production-center", filter: "select", priority: 1 },
      ),
      c.custom(
        "product",
        (row) => productById.get(row.product_id)?.code ?? `#${row.product_id}`,
        (row) => {
          const product = productById.get(row.product_id);
          return (
            <TwoLine
              primary={product?.code ?? `#${row.product_id}`}
              secondary={product?.category?.name}
            />
          );
        },
        { headerKey: "column.product-product", filter: "select", priority: 1 },
      ),
    ];
  }, [centerById, productById]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["print-settings", settingsId, "lines"] });
    await queryClient.invalidateQueries({ queryKey: ["print-settings", "lines", "all"] });
  };

  const addLines = async () => {
    if (!centerId || productIds.length === 0) return;
    try {
      await api.printSettingsLines.createBatch(
        productIds.map((productId) => ({
          id: 0,
          print_settings_id: settingsId,
          product_id: Number(productId),
          production_center_id: Number(centerId),
        })),
      );
      toast.success(t("notification.add-line.success", "Lines added successfully"));
      setAdding(false);
      setProductIds([]);
      await refresh();
    } catch (failure) {
      toast.error(
        failure instanceof Error ? failure.message : t("notification.add-line.error", "Failed to add line"),
      );
    }
  };

  const title = t("navigation.item.print-settings", "Print settings");
  if (!allowed) return <AccessDenied title={title} />;
  if (settings.error) return <ErrorState title={title} error={settings.error} />;

  const profile = settings.data;

  return (
    <>
      <PageHeader
        parent={{ to: "/config/print-settings", label: t("navigation.item.print-settings", "Print settings") }}
        title={`#${settingsId}`}
        badges={
          profile ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="info">{profile.border_type}</Badge>
              <Badge>{`Border H: ${profile.h_border_in_inches}"`}</Badge>
              <Badge>{`Border V: ${profile.v_border_in_inches}"`}</Badge>
              <Badge>{`Whitespace H: ${profile.h_white_space_in_inches}"`}</Badge>
              <Badge>{`Whitespace V: ${profile.v_white_space_in_inches}"`}</Badge>
              <Badge>{`Output: ${profile.output_type}`}</Badge>
              <Badge>{`Rotation: ${profile.rotation}°`}</Badge>
              <Badge>
                {`Max file size: ${
                  profile.maximum_file_size_mb === null || profile.maximum_file_size_mb === undefined
                    ? "∞ MB"
                    : `${profile.maximum_file_size_mb} MB`
                }`}
              </Badge>
            </div>
          ) : undefined
        }
      />

      <DataTable<PrintSettingsLineDTO>
        data={lines.data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={lines.isLoading}
        selectable
        actions={[
          {
            label: t("button.add.line", "Add line"),
            icon: <Plus className="size-4" />,
            variant: "primary",
            write: true,
            onClick: () => setAdding(true),
          },
        ]}
        onDeleteSelected={async (selected) => {
          await api.printSettingsLines.removeBatch(selected.map((row) => row.id));
          toast.success(t("notification.delete.success", "Items deleted successfully"));
          await refresh();
        }}
        deleteSelectedConfirm={() => ({
          title: t("dialog.confirm.delete.title", "Confirm delete"),
          message: t("dialog.confirm.delete.text", "Are you sure you want to delete the selected items?"),
        })}
      />

      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title={t("dialog.add-lines.title", "Add lines")}
        actions={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={!centerId || productIds.length === 0}
              onClick={() => void addLines()}
            >
              {t("dialog.confirm.add", "Add")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label={t("form.field.production-center", "Production center")} required>
            <Select
              value={centerId}
              placeholder={t("form.field.select", "Select")}
              options={(centers.data ?? []).map((entry) => ({
                value: String(entry.id),
                label: entry.name,
              }))}
              onChange={(event) => {
                setCenterId(event.target.value);
                setProductIds([]);
              }}
            />
          </Field>
          <Field label={t("form.field.product", "Product")} required>
            <MultiSelect
              options={availableProducts}
              value={productIds}
              onChange={setProductIds}
              disabled={!centerId}
            />
          </Field>
        </div>
      </Dialog>
    </>
  );
}
